import { InventoryItem } from "@turizi/shared";
import { useState, useMemo, useEffect } from "react";
import InventoryItemNode from "@/components/inventory-item-node";
import { useUpdateInventoryItem } from "@/hooks/api-hooks";


interface Props {
    items: InventoryItem[];
    onSelect?: (item: InventoryItem) => void;
    onRemove?: (item: InventoryItem) => void;
    selected?: number[];
    saving: boolean;
    loadingIds?: number[];
    onEditItem?: (item: InventoryItem) => void;
    onCreateChild?: (parent: InventoryItem) => void;
}

export default function InventoryItemsList({
    items = [],
    selected = [],
    onSelect,
    onRemove,
    saving,
    loadingIds = [],
    onEditItem,
    onCreateChild,
}: Props): JSX.Element {
    const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
    const [draggingItemId, setDraggingItemId] = useState<number | null>(null);
    const [isDragOverRoot, setIsDragOverRoot] = useState(false);

    const updateItem = useUpdateInventoryItem();

    const handleMoveItem = async (itemId: number, newParentId: number | null) => {
        try {
            await updateItem.mutateAsync({
                id: itemId,
                parent_id: newParentId,
            } as any);
        } catch (error) {
            console.error("Failed to move inventory item:", error);
        }
    };

    const handleDragOverRoot = (e: React.DragEvent) => {
        if (draggingItemId !== null) {
            const draggedItem = items.find(i => i.id === draggingItemId);
            if (draggedItem && draggedItem.parent_id !== null) {
                e.preventDefault();
                setIsDragOverRoot(true);
            }
        }
    };

    const handleDragLeaveRoot = () => {
        setIsDragOverRoot(false);
    };

    const handleDropRoot = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOverRoot(false);
        if (draggingItemId !== null) {
            handleMoveItem(draggingItemId, null);
        }
    };

    // Organize items by parent-child relationships
    const { itemsMap, childrenMap, rootItems } = useMemo(() => {
        const itemsMap = new Map<number, InventoryItem>();
        const childrenMap = new Map<number, InventoryItem[]>();
        const itemIds = new Set<number>();

        // First pass: collect all item IDs and build itemsMap
        items.forEach(item => {
            itemsMap.set(item.id, item);
            itemIds.add(item.id);
        });

        // Second pass: build childrenMap
        items.forEach(item => {
            if (item.parent_id) {
                if (!childrenMap.has(item.parent_id)) {
                    childrenMap.set(item.parent_id, []);
                }
                childrenMap.get(item.parent_id)!.push(item);
            }
        });

        // Third pass: identify root items (no parent or parent not in list)
        const rootItems: InventoryItem[] = [];
        items.forEach(item => {
            if (!item.parent_id || !itemIds.has(item.parent_id)) {
                rootItems.push(item);
            }
        });

        return { itemsMap, childrenMap, rootItems };
    }, [items]);

    // Initialize expandedItems with all parent items expanded by default
    useEffect(() => {
        const parentIds = new Set<number>();
        items.forEach(item => {
            if (childrenMap.has(item.id)) {
                parentIds.add(item.id);
            }
        });
        setExpandedItems(parentIds);
    }, [items, childrenMap]);

    const sortedItems = rootItems.sort((a, b) => a.name.localeCompare(b.name));

    // Toggle expansion of an item
    const toggleExpanded = (itemId: number) => {
        setExpandedItems(prev => {
            const newSet = new Set(prev);
            if (newSet.has(itemId)) {
                newSet.delete(itemId);
            } else {
                newSet.add(itemId);
            }
            return newSet;
        });
    };

    // Get children of an item
    const getChildren = (parentId: number): InventoryItem[] => {
        return childrenMap.get(parentId) || [];
    };

    // Check if item has children
    const hasChildren = (itemId: number): boolean => {
        return childrenMap.has(itemId) && childrenMap.get(itemId)!.length > 0;
    };

    return (
        <div
            onDragOver={handleDragOverRoot}
            onDragLeave={handleDragLeaveRoot}
            onDrop={handleDropRoot}
            className={`border rounded-xl shadow-xs overflow-hidden p-2 space-y-1 transition-all duration-155 ${isDragOverRoot
                    ? "border-dashed border-blue-400 bg-blue-50/20 ring-2 ring-blue-200"
                    : "border-slate-200/80 bg-white"
                }`}
        >
            {sortedItems.map((item) => (
                <InventoryItemNode
                    key={item.id}
                    depth={0}
                    item={item}
                    expandedItems={expandedItems}
                    toggleExpanded={toggleExpanded}
                    getChildren={getChildren}
                    hasChildren={hasChildren}
                    onSelect={onSelect}
                    onRemove={onRemove}
                    selected={selected}
                    saving={saving}
                    loadingIds={loadingIds}
                    onEditItem={onEditItem}
                    onCreateChild={onCreateChild}
                    draggingItemId={draggingItemId}
                    setDraggingItemId={setDraggingItemId}
                    onMoveItem={handleMoveItem}
                    items={items}
                />
            ))}
        </div>
    );
}